import { useCallback, useReducer, useRef } from 'react';

export type AgentEmptyEntrancePhase = 'idle' | 'playing' | 'complete';

/**
 * Resolves whether the full-page empty canvas should run its one-time entrance sequence.
 *
 * The sequence starts the first time the surface is empty on the full page. It does not replay on
 * ordinary rerenders, a return to empty after messages, or when history finishes loading into an
 * already-visible empty thread.
 */
export const resolveAgentEmptyEntrancePhase = (
  isFullPage: boolean,
  isEmptyThread: boolean,
  phase: AgentEmptyEntrancePhase
): AgentEmptyEntrancePhase => {
  if (!isFullPage) {
    return phase === 'playing' ? 'complete' : phase;
  }
  if (!isEmptyThread) {
    return phase === 'idle' ? phase : 'complete';
  }
  if (phase === 'idle') {
    return 'playing';
  }
  return phase;
};

export type AgentFullPageEmptyEntrance = {
  /** True while entrance motion should stay on the empty full-page chrome. */
  animateEntrance: boolean;
  /** Call when the entrance sequence finishes so rerenders do not replay it. */
  onEntranceComplete: () => void;
};

export function useAgentFullPageEmptyEntrance(isFullPage: boolean, isEmptyThread: boolean): AgentFullPageEmptyEntrance {
  const phaseRef = useRef<AgentEmptyEntrancePhase>('idle');
  const [, rerender] = useReducer((count: number) => count + 1, 0);

  phaseRef.current = resolveAgentEmptyEntrancePhase(isFullPage, isEmptyThread, phaseRef.current);

  const animateEntrance = phaseRef.current === 'playing' && isFullPage && isEmptyThread;

  const onEntranceComplete = useCallback(() => {
    if (phaseRef.current === 'playing') {
      phaseRef.current = 'complete';
      rerender();
    }
  }, []);

  return {
    animateEntrance,
    onEntranceComplete,
  };
}
